import { useMemo } from 'react';
import type { LeagueBundle, PlayersFile } from '../../shared/types';
import { Avatar, Empty, Loading, PageHead, Section, Stat } from '../components/common';
import { avatarUrl } from '../lib/format';
import { buildTeamViews, sortStandings } from '../lib/league';
import {
  agePivot,
  buildContention,
  PRIME_MAX,
  QUADRANTS,
  quadrantOf,
  YOUNG_MAX,
  type ContentionTeam,
  type Quadrant,
} from '../lib/contention';
import { Link } from '../lib/router';

/* --- Chart geometry, in viewBox units --------------------------------------
   The SVG scales to its container, so these are not pixels. A minimum width on
   the scroll wrapper keeps the axis labels legible on a phone instead of
   shrinking them to five points. */
const W = 760;
const H = 440;
const PAD = { l: 64, r: 28, t: 26, b: 50 };
const X0 = PAD.l;
const X1 = W - PAD.r;
const Y0 = PAD.t;
const Y1 = H - PAD.b;

interface Scales {
  ageLo: number;
  ageHi: number;
  netLo: number;
  netHi: number;
  sx: (age: number) => number;
  sy: (net: number) => number;
}

function makeScales(teams: ContentionTeam[], pivot: number): Scales {
  const ages = teams.map((t) => t.starterAge).filter((a): a is number => a != null);
  const rawLo = Math.min(pivot, ...ages);
  const rawHi = Math.max(pivot, ...ages);
  // A league whose rosters are all within a few months of each other would
  // otherwise render as a meaningless smear across the full width.
  const span = Math.max(3, rawHi - rawLo);
  const mid = (rawLo + rawHi) / 2;
  const ageLo = mid - span / 2 - 0.4;
  const ageHi = mid + span / 2 + 0.4;

  const maxAbs = Math.max(2, ...teams.map((t) => Math.abs(t.netPicks)));
  const netLo = -(maxAbs + 1);
  const netHi = maxAbs + 1;

  return {
    ageLo,
    ageHi,
    netLo,
    netHi,
    sx: (age) => X0 + ((age - ageLo) / (ageHi - ageLo)) * (X1 - X0),
    sy: (net) => Y1 - ((net - netLo) / (netHi - netLo)) * (Y1 - Y0),
  };
}

function ticks(lo: number, hi: number, step: number): number[] {
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) out.push(v);
  return out;
}

/**
 * Nudges a label down until it stops colliding with one already placed.
 *
 * Ten avatars on a two-axis plot will sooner or later stack, and two team names
 * printed on top of each other is worse than one printed slightly low.
 */
function makeLabelPlacer() {
  const placed: Array<{ x: number; y: number; w: number }> = [];
  return (cx: number, base: number, width: number): number => {
    let y = base;
    for (let step = 0; step < 4; step += 1) {
      y = base + step * 13;
      const clash = placed.some(
        (p) => Math.abs(p.x - cx) < (p.w + width) / 2 + 5 && Math.abs(p.y - y) < 11,
      );
      if (!clash) break;
    }
    placed.push({ x: cx, y, w: width });
    return y;
  };
}

const QUAD_FILL: Record<Quadrant, string> = {
  'win-now': 'rgba(242, 118, 46, 0.07)',
  stacked: 'rgba(244, 193, 75, 0.06)',
  rebuilding: 'rgba(63, 191, 127, 0.07)',
  'young-core': 'rgba(86, 168, 245, 0.06)',
};

const QUAD_TEXT: Record<Quadrant, string> = {
  'win-now': 'var(--ember)',
  stacked: 'var(--gold)',
  rebuilding: 'var(--good)',
  'young-core': 'var(--wr)',
};

function Scatter({ teams, pivot }: { teams: ContentionTeam[]; pivot: number }) {
  const s = makeScales(teams, pivot);
  const px = s.sx(pivot);
  const pz = s.sy(0);
  const place = makeLabelPlacer();

  const plotted = [...teams]
    .filter((t) => t.starterAge != null)
    .sort((a, b) => s.sy(a.netPicks) - s.sy(b.netPicks));

  return (
    <svg
      className="scatter"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Teams plotted by average starter age against net draft pick capital"
    >
      {/* Quadrant washes. Top half owns extra picks, right half is older. */}
      <rect x={px} y={pz} width={X1 - px} height={Y1 - pz} fill={QUAD_FILL['win-now']} />
      <rect x={px} y={Y0} width={X1 - px} height={pz - Y0} fill={QUAD_FILL.stacked} />
      <rect x={X0} y={Y0} width={px - X0} height={pz - Y0} fill={QUAD_FILL.rebuilding} />
      <rect x={X0} y={pz} width={px - X0} height={Y1 - pz} fill={QUAD_FILL['young-core']} />

      {ticks(s.netLo, s.netHi, s.netHi > 6 ? 2 : 1).map((v) => (
        <g key={`y${v}`}>
          <line x1={X0} x2={X1} y1={s.sy(v)} y2={s.sy(v)} stroke="var(--line-soft)" strokeWidth={1} />
          <text x={X0 - 10} y={s.sy(v) + 4} textAnchor="end" className="ax-tick">
            {v > 0 ? `+${v}` : v}
          </text>
        </g>
      ))}

      {ticks(s.ageLo, s.ageHi, 1).map((v) => (
        <g key={`x${v}`}>
          <line x1={s.sx(v)} x2={s.sx(v)} y1={Y0} y2={Y1} stroke="var(--line-soft)" strokeWidth={1} />
          <text x={s.sx(v)} y={Y1 + 20} textAnchor="middle" className="ax-tick">
            {v}
          </text>
        </g>
      ))}

      {/* The two axes that define the quadrants. */}
      <line x1={X0} x2={X1} y1={pz} y2={pz} stroke="var(--line)" strokeWidth={1.5} />
      <line x1={px} x2={px} y1={Y0} y2={Y1} stroke="var(--line)" strokeWidth={1.5} strokeDasharray="5 4" />

      <text x={px} y={Y0 - 9} textAnchor="middle" className="ax-note">
        league average · {pivot.toFixed(1)}y
      </text>

      <text x={X0 + 10} y={Y0 + 20} className="quad-label" fill={QUAD_TEXT.rebuilding}>
        REBUILDING
      </text>
      <text x={X1 - 10} y={Y0 + 20} textAnchor="end" className="quad-label" fill={QUAD_TEXT.stacked}>
        STACKED
      </text>
      <text x={X0 + 10} y={Y1 - 10} className="quad-label" fill={QUAD_TEXT['young-core']}>
        YOUNG CORE
      </text>
      <text x={X1 - 10} y={Y1 - 10} textAnchor="end" className="quad-label" fill={QUAD_TEXT['win-now']}>
        WIN-NOW
      </text>

      <text x={(X0 + X1) / 2} y={H - 8} textAnchor="middle" className="ax-title">
        Average age of the starting lineup →
      </text>
      <text
        x={-(Y0 + Y1) / 2}
        y={16}
        transform="rotate(-90)"
        textAnchor="middle"
        className="ax-title"
      >
        Net draft picks →
      </text>

      {plotted.map((t) => {
        const cx = s.sx(t.starterAge as number);
        const cy = s.sy(t.netPicks);
        const url = avatarUrl(t.view.avatar);
        const label = t.view.name.length > 15 ? `${t.view.name.slice(0, 14)}…` : t.view.name;
        const width = label.length * 5.4;
        const lx = Math.min(Math.max(cx, X0 + width / 2), X1 - width / 2);
        const ly = place(lx, cy + 27, width);
        const clip = `clip-${t.view.roster_id}`;

        return (
          <g key={t.view.roster_id}>
            <title>
              {`${t.view.name} — ${(t.starterAge as number).toFixed(1)}y starters, ${
                t.netPicks > 0 ? '+' : ''
              }${t.netPicks} net picks`}
            </title>
            <clipPath id={clip}>
              <circle cx={cx} cy={cy} r={14} />
            </clipPath>
            {url ? (
              <image
                href={url}
                x={cx - 14}
                y={cy - 14}
                width={28}
                height={28}
                clipPath={`url(#${clip})`}
                preserveAspectRatio="xMidYMid slice"
              />
            ) : (
              <circle cx={cx} cy={cy} r={14} fill="var(--bg-raised)" />
            )}
            {!url && (
              <text x={cx} y={cy + 5} textAnchor="middle" className="ax-tick">
                {t.view.name.slice(0, 1).toUpperCase()}
              </text>
            )}
            <circle cx={cx} cy={cy} r={14} fill="none" stroke="var(--line)" strokeWidth={1.5} />
            <text x={lx} y={ly} textAnchor="middle" className="dot-label">
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function AgeBar({ team }: { team: ContentionTeam }) {
  const { young, prime, old } = team.buckets;
  const total = Math.max(1, team.aged);
  const pct = (n: number) => `${(n / total) * 100}%`;
  return (
    <div
      className="agebar"
      title={`${young} aged ${YOUNG_MAX} or under · ${prime} aged ${YOUNG_MAX + 1}–${PRIME_MAX} · ${old} aged ${PRIME_MAX + 1}+`}
    >
      <span className="ab-young" style={{ width: pct(young) }} />
      <span className="ab-prime" style={{ width: pct(prime) }} />
      <span className="ab-old" style={{ width: pct(old) }} />
    </div>
  );
}

export default function Contention({
  bundle,
  players,
}: {
  bundle: LeagueBundle;
  players: PlayersFile | null;
}) {
  const views = useMemo(() => sortStandings(buildTeamViews(bundle)), [bundle]);
  const teams = useMemo(() => buildContention(bundle, views, players), [bundle, views, players]);
  const pivot = useMemo(() => agePivot(teams), [teams]);

  // The player dictionary is the whole feature here, unlike elsewhere on the
  // site where it only enriches a roster list.
  if (!players) return <Loading label="Loading the player dictionary" />;

  if (pivot == null) {
    return (
      <>
        <PageHead title="Contention Window" />
        <div className="card">
          <Empty>No starting lineups set yet.</Empty>
        </div>
      </>
    );
  }

  const byQuadrant = new Map<Quadrant, ContentionTeam[]>();
  for (const t of teams) {
    const q = quadrantOf(t, pivot);
    byQuadrant.set(q, [...(byQuadrant.get(q) ?? []), t]);
  }

  const oldest = [...teams]
    .filter((t) => t.starterAge != null)
    .sort((a, b) => (b.starterAge as number) - (a.starterAge as number));
  const mostPicks = [...teams].sort((a, b) => b.netPicks - a.netPicks);
  const mostYouth = [...teams].sort((a, b) => b.buckets.young - a.buckets.young);

  return (
    <>
      <PageHead
        title="Contention Window"
        meta={[
          'Starter age against net pick capital',
          `League average ${pivot.toFixed(1)}y`,
          'K and DEF excluded',
        ]}
      />

      {/* grid-4, not grid-3: these are four <Stat> tiles, the same component
          Home, Luck and TeamDetail lay out with grid-4. Its 170px track goes
          two-up on a phone where grid-3's 230px cannot, which is why this page
          alone spent 453px of vertical scroll on what costs the others 220. */}
      <div className="grid grid-4">
        <Stat
          label="Oldest lineup"
          value={oldest[0]?.view.name ?? '—'}
          note={oldest[0]?.starterAge != null ? `${oldest[0].starterAge.toFixed(1)} years` : undefined}
        />
        <Stat
          label="Youngest lineup"
          value={oldest[oldest.length - 1]?.view.name ?? '—'}
          note={
            oldest[oldest.length - 1]?.starterAge != null
              ? `${(oldest[oldest.length - 1].starterAge as number).toFixed(1)} years`
              : undefined
          }
        />
        <Stat
          label="Most pick capital"
          value={mostPicks[0]?.view.name ?? '—'}
          note={
            mostPicks[0]
              ? `${mostPicks[0].netPicks > 0 ? '+' : ''}${mostPicks[0].netPicks} net · ${
                  mostPicks[0].heldPicks
                } held`
              : undefined
          }
        />
        <Stat
          label="Deepest youth pile"
          value={mostYouth[0]?.view.name ?? '—'}
          note={mostYouth[0] ? `${mostYouth[0].buckets.young} players ${YOUNG_MAX} or under` : undefined}
        />
      </div>

      <Section title="The Window">
        <div className="card chart-wrap">
          <Scatter teams={teams} pivot={pivot} />
        </div>
        <p className="faint" style={{ fontSize: 12, marginTop: 10 }}>
          Divides are the league's own average starter age and zero net picks.
        </p>
      </Section>

      <Section title="Where Everyone Sits">
        <div className="grid grid-2 grid-start">
          {(Object.keys(QUADRANTS) as Quadrant[]).map((q) => {
            const list = (byQuadrant.get(q) ?? []).sort(
              (a, b) => (b.starterAge ?? 0) - (a.starterAge ?? 0),
            );
            return (
              <article className="card card-pad" key={q}>
                <div className="quad-head" style={{ color: QUAD_TEXT[q] }}>
                  {QUADRANTS[q].label}
                  <span className="faint" style={{ marginLeft: 'auto', fontSize: 12 }}>
                    {list.length} team{list.length === 1 ? '' : 's'}
                  </span>
                </div>
                <p className="faint" style={{ fontSize: 12, margin: '4px 0 10px' }}>
                  {QUADRANTS[q].blurb}
                </p>
                {list.length === 0 ? (
                  <div className="faint" style={{ fontSize: 12.5 }}>
                    Nobody.
                  </div>
                ) : (
                  list.map((t) => (
                    <Link to={`/teams/${t.view.roster_id}`} key={t.view.roster_id}>
                      <div className="player-row">
                        <Avatar id={t.view.avatar} alt={t.view.name} />
                        <span className="player-name">{t.view.name}</span>
                        <span className="player-meta">
                          {t.starterAge != null ? `${t.starterAge.toFixed(1)}y` : '—'}
                          {' · '}
                          {t.netPicks > 0 ? '+' : ''}
                          {t.netPicks}
                        </span>
                      </div>
                    </Link>
                  ))
                )}
              </article>
            );
          })}
        </div>
      </Section>

      <Section title="Roster Age" hint="Players with a known age">
        <div className="table-scroll">
          <table className="age-table">
            <thead>
              <tr>
                <th>Team</th>
                <th className="num">Starters</th>
                <th>Distribution</th>
                <th className="num">≤{YOUNG_MAX}</th>
                <th className="num">
                  {YOUNG_MAX + 1}–{PRIME_MAX}
                </th>
                <th className="num">{PRIME_MAX + 1}+</th>
                <th>Oldest starter</th>
                <th>Youngest starter</th>
                <th className="num">Net picks</th>
              </tr>
            </thead>
            <tbody>
              {[...teams]
                .sort((a, b) => (b.starterAge ?? 0) - (a.starterAge ?? 0))
                .map((t) => (
                  <tr key={t.view.roster_id}>
                    <td>
                      <Link to={`/teams/${t.view.roster_id}`}>
                        <div className="team-cell">
                          <Avatar id={t.view.avatar} alt={t.view.name} />
                          <span className="team-title">{t.view.name}</span>
                        </div>
                      </Link>
                    </td>
                    <td className="num" title={`${t.agedStarters} starters counted`}>
                      {t.starterAge != null ? `${t.starterAge.toFixed(1)}y` : '—'}
                    </td>
                    <td>
                      <AgeBar team={t} />
                    </td>
                    <td className="num">{t.buckets.young}</td>
                    <td className="num">{t.buckets.prime}</td>
                    <td className="num">{t.buckets.old}</td>
                    <td className="nowrap">
                      {t.oldest ? (
                        <>
                          {t.oldest.name} <span className="faint">{t.oldest.age}y</span>
                        </>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td className="nowrap">
                      {t.youngest ? (
                        <>
                          {t.youngest.name} <span className="faint">{t.youngest.age}y</span>
                        </>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td className="num">
                      <span
                        className={`pill ${
                          t.netPicks > 0 ? 'pill-good' : t.netPicks < 0 ? 'pill-bad' : ''
                        }`}
                      >
                        {t.netPicks > 0 ? '+' : ''}
                        {t.netPicks}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="legend">
          <span>
            <i className="ab-young" /> {YOUNG_MAX} and under
          </span>
          <span>
            <i className="ab-prime" /> {YOUNG_MAX + 1}–{PRIME_MAX}
          </span>
          <span>
            <i className="ab-old" /> {PRIME_MAX + 1} and over
          </span>
          <span className="faint">
            Players with no age on Sleeper are left out of the mean, not counted as zero.
          </span>
        </div>
      </Section>
    </>
  );
}
